// Observed selector resolver for suffix markers like `.item?` and `.item?!`.
// Marker must be attached directly to selector tail without whitespace.
import { joinSelectorParts, splitTopLevelSelectorList } from './selector-list'

export type ObservedSelectorMode = 'none' | 'dynamic' | 'initial'

export type ObservedSelectorResolution = {
  selector: string
  observedMode: ObservedSelectorMode
}

export type ObservedSelectorPartResolution = ObservedSelectorResolution & {
  hasMarker: boolean
  markerValid: boolean
}

type ObservedSuffixResolution = {
  _mode: Exclude<ObservedSelectorMode, 'none'>
  _markerLength: 1 | 2
}

// Resolve observed marker suffix at selector end.
function resolveObservedSuffix(selector: string): ObservedSuffixResolution | null {
  if (selector.endsWith('?!')) return { _mode: 'initial', _markerLength: 2 }
  if (selector.endsWith('?')) return { _mode: 'dynamic', _markerLength: 1 }
  return null
}

// Resolve one selector-list part and strip a valid observed suffix.
export function resolveObservedSelectorPart(selector: string): ObservedSelectorPartResolution {
  const trimmed = selector.trim()
  const observedSuffix = resolveObservedSuffix(trimmed)
  if (!observedSuffix) {
    return { selector: trimmed, observedMode: 'none', hasMarker: false, markerValid: false }
  }

  const markerStartIndex = trimmed.length - observedSuffix._markerLength
  const markerAttachedChar = trimmed[markerStartIndex - 1]
  // Matches whitespace before an observed marker. Example match: " ".
  if (!markerAttachedChar || /\s/.test(markerAttachedChar)) {
    return { selector: trimmed, observedMode: 'none', hasMarker: true, markerValid: false }
  }

  const normalizedSelector = trimmed.slice(0, markerStartIndex)
  if (!hasValidObservedSelectorTail(normalizedSelector)) {
    return { selector: trimmed, observedMode: 'none', hasMarker: true, markerValid: false }
  }

  return {
    selector: normalizedSelector,
    observedMode: observedSuffix._mode,
    hasMarker: true,
    markerValid: true,
  }
}

// Resolve every top-level selector-list part independently.
export function resolveObservedSelectorParts(selector: string): ObservedSelectorPartResolution[] {
  return splitTopLevelSelectorList(selector).map((part) => resolveObservedSelectorPart(part))
}

// Strip valid observed suffixes while preserving static selector-list parts.
export function stripObservedMarkersFromSelector(selector: string): string {
  return joinSelectorParts(resolveObservedSelectorParts(selector).map((part) => part.selector))
}

// Normalize selector suffix and resolve observed flag for all-observed lists.
export function resolveObservedSelector(selector: string): ObservedSelectorResolution {
  const parts = resolveObservedSelectorParts(selector)
  let resolvedMode: ObservedSelectorMode = 'none'

  for (const part of parts) {
    if (part.observedMode === 'none') {
      return { selector: stripObservedMarkersFromSelector(selector), observedMode: 'none' }
    }

    if (resolvedMode === 'none') {
      resolvedMode = part.observedMode
    } else if (resolvedMode !== part.observedMode) {
      return { selector: stripObservedMarkersFromSelector(selector), observedMode: 'none' }
    }
  }

  return {
    selector: joinSelectorParts(parts.map((part) => part.selector)),
    observedMode: resolvedMode,
  }
}

// Validate selector tail char before observed marker stripping.
function hasValidObservedSelectorTail(selector: string): boolean {
  const trimmed = selector.trimEnd()
  if (!trimmed) return false

  const tail = trimmed[trimmed.length - 1]
  return tail !== '>' && tail !== '+' && tail !== '~' && tail !== ','
}
